import { useCollapsePanel } from "hooks/useCollapsePanel";
import { v4 as uuidv4 } from "uuid";
import { convertToTitleCase, getName } from "js/formatting";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import { isFlagEnabled, getAllInsuredsFromParties } from "js/utilities";
import Tooltip from "components/General/Tooltip";
import { useTranslation } from "react-i18next";

const InsuredCard = ({ cardTitle, effectiveDate, policyDetail }) => {
  const { t } = useTranslation();
  const uuid = uuidv4();
  useCollapsePanel(uuid);
  const { parties, smokeCodeDescription } = policyDetail;
  let insuredPersons = getAllInsuredsFromParties(parties);

  return (
    <div className="ui-card">
      <div className="px-4 pt-3 md:flex-grow xl:px-6 xl:pt-6">
        <div className="md:flex md:flex-row md:items-start">
          <h2 className="mb-0">
            <button
              id={uuid}
              className="mb-0 flex items-center group md:hidden"
              aria-controls={`${uuid}-panel`}
              aria-expanded="true"
              aria-label={t("AgentInsuredCard-expand-collapse-btn-label")}
            >
              <span className="inline-block p-1 rounded-full mr-1 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                <svg
                  className="ui-icon ui-plus w-4 h-4"
                  focusable="false"
                  aria-hidden="true"
                  role="presentation"
                >
                  <use href="#ui-icon-plus"></use>
                </svg>
                <svg
                  className="ui-icon ui-minus w-4 h-4"
                  focusable="false"
                  aria-hidden="true"
                  role="presentation"
                >
                  <use href="#ui-icon-minus"></use>
                </svg>
              </span>
              <span className="text-base font-body font-bold">{cardTitle}</span>
            </button>
            <span className="text-xl font-body font-bold hidden md:block">
              {cardTitle}
            </span>
          </h2>
        </div>
        <hr className="mb-0 mt-3 md:hidden" />
      </div>
      <hr className="hidden md:block" />
      <div
        id={`${uuid}-panel`}
        className="px-4 pb-6 overflow-hidden h-20 mt-4 md:h-auto md:mt-0 xl:px-6 xl:pb-6"
      >
        {insuredPersons.map((insured, idx) => {
          const isPrimary = insured.person.partyTypeCode === "IN";
          return (
            <>
              <div
                key={`insured detail card - ${insured.person}`}
                className={`grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-6 ${
                  idx !== insuredPersons.length - 1 && "mb-6"
                }`}
              >
                <div>
                  <div className="text-xs text-theme-text-tertiary flex items-center">
                    {t("AgentOwnerInsuredCard-label-1")}
                  </div>
                  <div className="text-sm text-theme-text-secondary font-semibold break-words">
                    {getName(insured.person)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-theme-text-tertiary flex items-center">
                    {t("AgentOwnerInsuredCard-label-2")}
                  </div>
                  <div className="text-sm text-theme-text-secondary font-semibold break-words">
                    {insured.person.dateofBirth}
                  </div>
                </div>
                {isPrimary && (
                  <div>
                    <div className="text-xs text-theme-text-tertiary flex items-center">
                      {t("AgentOwnerInsuredCard-label-3")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold break-words">
                      {policyDetail?.insuredIssueAge}
                    </div>
                  </div>
                )}
                {isFlagEnabled(
                  "ENABLE_RATE_CLASS_AND_FLAT_EXTRA_PREMIUM_FIELDS"
                ) &&
                  smokeCodeDescription && (
                    <div>
                      <div className="text-xs text-theme-text-tertiary flex items-center">
                        {t("AgentOwnerInsuredCard-label-4")}
                      </div>
                      <div className="text-sm text-theme-text-secondary font-semibold break-words">
                        {convertToTitleCase(smokeCodeDescription)}
                      </div>
                    </div>
                  )}
                {insured.person?.permanentTableRatingCode && (
                  <div>
                    <div className="text-xs text-theme-text-tertiary flex items-center">
                      {t("AgentOwnerInsuredCard-label-5")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold break-words">
                      {insured.person?.permanentTableRatingCode}
                    </div>
                  </div>
                )}
                {isFlagEnabled(
                  "ENABLE_RATE_CLASS_AND_FLAT_EXTRA_PREMIUM_FIELDS"
                ) &&
                  insured.person?.temporaryflatextraAmount && (
                    <div>
                      <div className="text-xs text-theme-text-tertiary flex items-center">
                        {t("AgentOwnerInsuredCard-label-6")}
                        <Tooltip
                          placement="right"
                          xMargin="mx-0"
                          content="This amount is per thousand dollars of coverage"
                        />
                      </div>
                      <div className="text-sm text-theme-text-secondary font-semibold break-words">
                        {insured.person?.temporaryflatextraAmount}
                      </div>
                    </div>
                  )}
                {insured.person?.substandardExpiryDate && (
                  <div>
                    <div className="text-xs text-theme-text-tertiary flex items-center">
                      {t("AgentOwnerInsuredCard-label-7")}
                    </div>
                    <div className="text-sm text-theme-text-secondary font-semibold break-words">
                      {insured.person?.substandardExpiryDate}
                    </div>
                  </div>
                )}
              </div>
              {idx !== insuredPersons.length - 1 && (
                <hr className="hidden md:block" />
              )}
            </>
          );
        })}
      </div>
      <CardDisclaimerBase disclaimer={t("AgentOwnerInsuredCard-dsc")} />
    </div>
  );
};
export default InsuredCard;
